/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2024. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: ZtnMessage.ts
 * Created by: ChanYing
 * Create Date: 2024-03-18 10:50
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import {createApp, h} from 'vue'
import AvTime from "@/assets/icons/message/av-time.vue";
import '@/assets/design/css/ztn-message.css'

export enum MessageType {
    none,
    primary,
    error,
    success,
    warning,
    info
}

interface MessageInstance {
    close: () => void
}

interface MessageOptions {
    message: string
    type?: MessageType
    customClass?: string
    duration?: number
    showClose?: boolean
    center?: boolean
    onClose?: () => void
}

const messageContainer = document.createElement('div')
messageContainer.className = 'ztn-message-container'
document.body.appendChild(messageContainer)

const messages: Array<{ element: HTMLDivElement; close: () => void }> = []

function getMessageTypeClass(type: MessageType): string {
    switch (type) {
        case MessageType.primary:
            return 'ztn-message-primary'
        case MessageType.error:
            return 'ztn-message-error'
        case MessageType.warning:
            return 'ztn-message-warning'
        case MessageType.success:
            return 'ztn-message-success'
        case MessageType.info:
            return 'ztn-message-info'
        default:
            return ''
    }
}

export function ztnMessage(options: string | MessageOptions): MessageInstance {
    let msg: string
    let type: MessageType = MessageType.info
    let customClass = 'ztn-message ztn-page-h600 '
    let duration = 3000
    let showClose = true
    let onClose: (() => void) | undefined

    if (typeof options === 'string') {
        msg = options
    } else {
        msg = options.message
        type = options.type || MessageType.info
        customClass = options.customClass || customClass
        duration = options.duration ?? 3000
        showClose = options.showClose ?? true
        onClose = options.onClose
    }

    customClass += getMessageTypeClass(type)

    const icon = h(AvTime, {class: 'ztn-message-icon'})
    const text = h('span', {class: 'ztn-message-text'}, msg)
    const closeBtn = showClose
        ? h('a', {class: 'ztn-message-close', onClick: () => instance.close()}, '×')
        : null

    const vnode = h('div', {
        class: customClass,
    }, [icon, text, closeBtn])

    const app = createApp({render: () => vnode})
    const container = document.createElement('div')
    container.className = 'ztn-message-wrapper'
    app.mount(container)
    messageContainer.appendChild(container)

    const instance: MessageInstance = {
        close() {
            onClose?.()
            app.unmount()
            container.remove()
            const idx = messages.findIndex(m => m.element === container)
            if (idx !== -1) {
                messages.splice(idx, 1)
            }
        }
    }

    messages.push({element: container, close: instance.close})

    if (duration > 0) {
        setTimeout(() => {
            instance.close()
        }, duration)
    }

    return instance
}

export const verifyMessage = function (message: string, type: MessageType, className: string, errorClassName: string) {
    let element = document.querySelector("." + className);
    try {
        if (element !== null) {
            element.scrollIntoView({behavior: "auto", block: "start"});
        }
        ztnMessage({message, type});
    } finally {
        if (element !== null) {
            element.classList.add(errorClassName);
            setTimeout(function () {
                if (element !== null) {
                    element.classList.remove(errorClassName);
                }
            }, 1000);
        }
    }
}
